import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useUiStrings } from '@/hooks/useUiStrings'
import { useStore } from '@/store/useStore'
import { SETTINGS_BACKLOG_PATH } from '@/constants/settings-tabs'
import { useBacklogConnectNudge } from '@/hooks/useBacklogConnectNudge'
import { SparklesIcon } from './Icons'

/**
 * One-time notice that meetings can use the reader's Backlog account.
 *
 * Shown the first time the reader lands on Home without a working connection.
 * Skip stores that choice. A connection that is already active does the same,
 * so the dialog does not return after a later disconnect.
 */
export default function BacklogConnectNudge() {
  const { t } = useUiStrings()
  const needsConnection = useBacklogConnectNudge()
  const dismissed = useStore((s) => s.backlogFeatureNoticeDismissed)
  const dismissBacklogNudge = useStore((s) => s.dismissBacklogNudge)

  useEffect(() => {
    if (dismissed || !needsConnection) return undefined
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') dismissBacklogNudge()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [dismissBacklogNudge, dismissed, needsConnection])

  if (dismissed || !needsConnection) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/30 px-3 py-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="backlog-connect-nudge-title"
      data-testid="backlog-connect-nudge"
    >
      <button
        type="button"
        aria-label={t.close}
        className="fixed inset-0 cursor-default"
        onClick={dismissBacklogNudge}
      />

      <div
        className="animate-scale-in relative w-full max-w-md rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-5 shadow-2xl sm:p-6"
        data-testid="backlog-connect-nudge-card"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[var(--accent)] dark:bg-blue-500/15">
            <SparklesIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2
              id="backlog-connect-nudge-title"
              className="text-lg font-bold text-[var(--text-primary)]"
            >
              {t.backlogNudgeTitle}
            </h2>
            <p className="mt-2 text-sm leading-5 text-[var(--text-secondary)]">
              {t.backlogNudgeBody}
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={dismissBacklogNudge}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)]"
          >
            {t.backlogNudgeSkip}
          </button>
          <Link
            to={SETTINGS_BACKLOG_PATH}
            onClick={dismissBacklogNudge}
            className="inline-flex items-center rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[var(--accent-hover)]"
          >
            {t.backlogNudgeAction}
          </Link>
        </div>
      </div>
    </div>
  )
}
